/* eslint-disable */
import React, { useRef, useState } from "react";
import { Upload, FileSpreadsheet, X, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Reusable file upload card for IT Officer pages.
 *
 * Props:
 *  title        - card heading
 *  description  - subtitle
 *  onUpload     - fn(file, extraData) called when user submits
 *  extras       - array of { name, label, type, placeholder, required } for extra fields (e.g. date)
 *  isLoading    - bool
 *  response     - { state: "SUCCESS"|"ERROR", message: string }
 *  accentColor  - tailwind color key: "cyan"|"blue"|"emerald"|"purple"|"orange" (default "cyan")
 */
function UploadCard({ title, description, onUpload, extras = [], isLoading, response, accentColor = "cyan" }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [extraData, setExtraData] = useState({});
  const [dragOver, setDragOver] = useState(false);

  const accent = {
    cyan:    { zone: "border-cyan-400 bg-cyan-50/60 dark:bg-cyan-900/10",    btn: "bg-cyan-600 hover:bg-cyan-700", text: "text-cyan-600 dark:text-cyan-400", ring: "focus:ring-cyan-500" },
    blue:    { zone: "border-blue-400 bg-blue-50/60 dark:bg-blue-900/10",    btn: "bg-blue-600 hover:bg-blue-700", text: "text-blue-600 dark:text-blue-400", ring: "focus:ring-blue-500" },
    emerald: { zone: "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/10", btn: "bg-emerald-600 hover:bg-emerald-700", text: "text-emerald-600 dark:text-emerald-400", ring: "focus:ring-emerald-500" },
    purple:  { zone: "border-purple-400 bg-purple-50/60 dark:bg-purple-900/10", btn: "bg-purple-600 hover:bg-purple-700", text: "text-purple-600 dark:text-purple-400", ring: "focus:ring-purple-500" },
    orange:  { zone: "border-orange-400 bg-orange-50/60 dark:bg-orange-900/10", btn: "bg-orange-600 hover:bg-orange-700", text: "text-orange-600 dark:text-orange-400", ring: "focus:ring-orange-500" },
  }[accentColor] || { zone: "border-cyan-400 bg-cyan-50/60", btn: "bg-cyan-600 hover:bg-cyan-700", text: "text-cyan-600", ring: "focus:ring-cyan-500" };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    onUpload(file, extraData);
  };

  const clear = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="max-w-xl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 dark:border-gray-700">
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{title}</h3>
          {description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Extra fields (e.g. date picker) */}
          {extras.map((field) => (
            <div key={field.name}>
              <label className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                {field.label}
              </label>
              <input
                type={field.type || "text"}
                name={field.name}
                placeholder={field.placeholder || ""}
                required={field.required}
                value={extraData[field.name] || ""}
                onChange={(e) => setExtraData({ ...extraData, [field.name]: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 ${accent.ring} focus:border-transparent outline-none transition-all`}
              />
            </div>
          ))}

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer ${dragOver ? accent.zone : "border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500"}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />

            {file ? (
              <div className="flex items-center gap-3 p-4">
                <FileSpreadsheet size={32} className={accent.text} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{file.name}</p>
                  <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clear(); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Upload size={28} className="text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Drop your file here, or <span className={`underline ${accent.text}`}>browse</span>
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">.xlsx, .xls, .csv supported</p>
              </div>
            )}
          </div>

          {/* Response message */}
          {response?.state === "SUCCESS" && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800">
              <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">{response.message}</p>
            </div>
          )}
          {response?.state === "ERROR" && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{response.message}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!file || isLoading}
            className={`w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-all shadow-sm ${accent.btn} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? "Uploading..." : "Upload File"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UploadCard;
